import React, { useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faMinusCircle, faPlusCircle } from '@fortawesome/free-solid-svg-icons';

import useQLEMN from './hooks/useQLEMN';
import { modalConfig } from './modals/modalConfig';
import AlertInfo from '@components/common/alert/AlertInfo';
import BackToDashboard from '@components/common/backToDashboard';
import { useAppDispatch, useAppSelector, useModal } from '@hooks/index';
import AccordionPanel from '@components/common/app-components/AccordionPanel';
import VerificationsTab from '@components/documentsVerification/VerificationsTab';
import { IQLEVerificationDocumentResponse, VALIDATION_STATUS } from './types/qleTypes';
import { ConditionalRendering, configs, GIButton, withModalWrapper } from '../members/utils';
import { QleAccordionHeaderContainer, StyledAccordionContainer, StyledAccordionHeader, StyledAlertInfoContainer, StyledBackButtonWrapper, StyledContainerWrapper, StyledDetailsContainer, StyledHeader, StyledOverrideButton, StyledSubheader, StyledSubheaderContainer, StyledUserInfo, StyledVerificationsContainer } from '@constants/styledComponents/DMIStyledComponents';

const QLEDocumentsContainer = () => {
    const dispatch = useAppDispatch();
    const {
        fetchQLEVerificationsData,
        filterApplicants,
        areAllEventsVerified,
        getDocumentVerirficationStatus,
        getDocumentVerificationIcon,
        isOverrideButtonVisible,
        isDocumentVerified,
    } = useQLEMN();
    const intl = useIntl();
    const { openModal } = useModal();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const { verificationsData }: { verificationsData: IQLEVerificationDocumentResponse } = useAppSelector(
        state => state.qle,
    );
    const { activeTab, dashboardModels } = useAppSelector(state => state.individualPortal.dashboard);
    const { caseNumber } = dashboardModels[activeTab] || {};
    const queryParams = new URLSearchParams(location?.search);
    const effectiveCaseNumber = caseNumber || queryParams.get('caseNumber');
    const link = intl.formatMessage({ id: 'qleMN_.link' });

    const applicants = verificationsData?.applicants?.filter(filterApplicants);

    useEffect(() => {
        dispatch(
            fetchQLEVerificationsData(
                configs.endpoints.QLE_DOCUMENTS.processReplacements({
                    eventId: effectiveCaseNumber,
                }),
            ),
        );
    }, []);

    const handleOverrideModal = event => {
        openModal('overrideQLEDocumentsModal', {
            applicantEventId: event?.applicantEventId,
            caseNumber: effectiveCaseNumber,
        });
    };

    return (
        <StyledVerificationsContainer className="grid-container gi-grid-container">
            <StyledBackButtonWrapper>
                <BackToDashboard />
            </StyledBackButtonWrapper>
            <StyledHeader data-testid={'qle-verifications-header'}>
                <FormattedMessage id="dmi.applicantVerifications.header" />
            </StyledHeader>
            <StyledSubheaderContainer>
                <StyledSubheader data-testid={'qle-verifications-subHeader'}>
                    <FormattedMessage id="qleMN_.header" />
                </StyledSubheader>
            </StyledSubheaderContainer>
            <StyledContainerWrapper>
                <StyledUserInfo data-testid={'qle-verifications-applicants'}>
                    {applicants?.map((applicant, index) => (
                        <VerificationsTab
                            name={`${applicant.firstName} ${applicant.lastName}`}
                            index={index}
                            selectedIndex={selectedIndex}
                            onSelect={() => setSelectedIndex(index)}
                            isDocumentCategoryVerified={areAllEventsVerified(applicant.events)}
                            key={index}
                            isCheckIconVisible
                        />
                    ))}
                </StyledUserInfo>
                <StyledDetailsContainer>
                    <StyledAlertInfoContainer data-testid={'qle-verifications-info'}>
                        <AlertInfo
                            id="qleMN_.alert"
                            textReplacements={{
                                linkForQLE: <a href={link} target='_blank'><FormattedMessage id='common.here' /></a>,
                            }}
                        />
                    </StyledAlertInfoContainer>

                    {applicants?.[selectedIndex]?.events?.map((event, eventIndex) => {
                        const documentVerificationStatus = getDocumentVerirficationStatus(event?.validationStatus);
                        const documentVerificationIcon = getDocumentVerificationIcon(event?.validationStatus);
                        return (
                            <StyledAccordionContainer key={`qle_documents_${selectedIndex}_${eventIndex}`} data-testid={
                                `qle-verifications-accordion-${selectedIndex}-${eventIndex}`
                            }>
                                <AccordionPanel
                                    id={`qle-verifications-accordion-${selectedIndex}-${eventIndex}`}
                                    header={
                                        <StyledAccordionHeader>
                                            <QleAccordionHeaderContainer isDocumentCategoryVerified={isDocumentVerified(event?.validationStatus)}>
                                                <p className="verification-type mr-2 h4">{event.eventLabel}</p>
                                                <ConditionalRendering isTrue={!!documentVerificationStatus}>
                                                    <p className="verification-type">
                                                        <FormattedMessage id={documentVerificationStatus} />
                                                    </p>
                                                </ConditionalRendering>
                                                <ConditionalRendering isTrue={!!documentVerificationIcon}>
                                                    <FontAwesomeIcon
                                                        icon={documentVerificationIcon}
                                                        className="icon ml-2 xs"
                                                    />
                                                </ConditionalRendering>
                                            </QleAccordionHeaderContainer>
                                            <ConditionalRendering
                                                isTrue={isOverrideButtonVisible(event?.validationStatus)}
                                            >
                                                <StyledOverrideButton>
                                                    <GIButton
                                                        data-variant="secondary"
                                                        icon={<FontAwesomeIcon icon={faEdit} />}
                                                        iconPlacement="start"
                                                        id={'button.override'}
                                                        isDisabled={false}
                                                        labelId="button.override"
                                                        name="override"
                                                        onClickHandler={() => handleOverrideModal(event)}
                                                        size="small"
                                                        type="button"
                                                        variant="danger"
                                                    />
                                                </StyledOverrideButton>
                                            </ConditionalRendering>
                                        </StyledAccordionHeader>
                                    }
                                    isExpandable={true}
                                    isLeftToggle={true}
                                    expandedIcon={faMinusCircle}
                                    collapsedIcon={faPlusCircle}
                                >
                                    <ConditionalRendering
                                        isTrue={event?.validationStatus === VALIDATION_STATUS.REJECTED}
                                    >
                                        <p className="alert alert-info margin10-t">
                                            <FormattedMessage id="qleMN_.document.rejected" />
                                        </p>
                                    </ConditionalRendering>
                                </AccordionPanel>
                            </StyledAccordionContainer>
                        );
                    })}
                </StyledDetailsContainer>
            </StyledContainerWrapper>
        </StyledVerificationsContainer>
    );
};

export default withModalWrapper(QLEDocumentsContainer, modalConfig);
